import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, combineLatest, EMPTY, Observable, of, ReplaySubject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

import { IsfV1Model } from '@data-layer/models/isf-v1';
import { enumPaymentState } from '@data-layer/enums/payments';
import { enumIsfState } from '@data-layer/enums/isf';
import { CompaniesService } from '@services/companies.service';
import { UserModel } from '@data-layer/models/user';
import { VerifyPhoneNumberComponent } from '@shared/verify-phone-number/components';
import { INPUT_TYPES_MAP } from '@constants/input-types-map';
import { CompanyModel } from '@data-layer/models/companies';
import { DeliveryOrderModel, EntrySummaryModel } from '@data-layer/models/entry-summaries';
import { IErrorForm } from '@shared/form-wrapper/interfaces';

import { AffiliateData, ICustomError } from '../../interfaces';
import { BondRequestModel } from '@data-layer/models/common';
import { VerificationCodeHelper } from '@helpers/verification-code.helper';
import { environment } from '@env/environment';
import { enumEnvironmentName } from '@env/enumEnvironmentName';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';
import { enumMarineInsuranceEntityType, enumMarineInsurancePendingPaymentAction } from '@app/data-layer/enums/marine-insurance';
import { MarineInsuranceModel } from '@data-layer/models/marine-insurance';
import { DocumentModel } from '@data-layer/models/documents';
import { PaymentBillItemPendingModel } from '@data-layer/models/payments';
import { MarineInsuranceSideformService, MarineInsuranceState } from '@shared/marine-insurance-shared/services';
import { EntrySummaryStateService } from '@shared/es-mutual/services';
import { IsfStateService } from '@shared/isf-mutual/services';
import { IsfModel } from '@data-layer/models/isf';

@Component({
  selector: 'app-result',
  templateUrl: './result.component.html',
  styleUrls: ['./result.component.scss'],
})
export class ResultComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public affiliateData: AffiliateData = null;
  @Input() public error: ICustomError = null;
  @Input() public warning: ICustomError = null;
  @Input() public currES: EntrySummaryModel = null;
  @Input() public currIsf: IsfV1Model = null;
  @Input() public currBondRequest: BondRequestModel = null;
  @Input() public isDelivery: boolean = false;
  @Input() public deliveryRouterLink: string;
  @Input() public deliveryOrders: DeliveryOrderModel[];
  @Output() public goToEdit: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild(VerifyPhoneNumberComponent, { static: false }) public verifyModal: VerifyPhoneNumberComponent;
  @ViewChild('hiddeninput', { static: false }) public hiddeninput: ElementRef;

  public isError: boolean = false;
  public isWarningRes: boolean = false;
  public isSuccessRes: boolean = false;
  public isPhoneUpdated: boolean = false;
  public user: UserModel;
  public company: CompanyModel;
  public formGroup: UntypedFormGroup;
  public errors: { [name: string]: IErrorForm } = {};
  public isChangedPhoneNumber: boolean = false;
  public integerInputMap: { mask: string; pattern: string } = INPUT_TYPES_MAP.INTEGER;

  public isProdEnv: boolean = environment.name === enumEnvironmentName.PROD;

  public enumUserBetaFlag: typeof enumUserBetaFlag = enumUserBetaFlag;
  public enumMarineInsuranceEntityType: typeof enumMarineInsuranceEntityType = enumMarineInsuranceEntityType;
  public enumPaymentState: typeof enumPaymentState = enumPaymentState;
  public enumMarineInsurancePendingPaymentAction: typeof enumMarineInsurancePendingPaymentAction = enumMarineInsurancePendingPaymentAction;

  private isDestroy: ReplaySubject<boolean> = new ReplaySubject();
  private formattedPhoneNumber: string;

  constructor(
    private router: Router,
    private companiesService: CompaniesService,
    private fb: UntypedFormBuilder,
    private isfStateService: IsfStateService,
    private entrySummaryStateService: EntrySummaryStateService,
    private marineInsuranceSideformService: MarineInsuranceSideformService,
    private marineInsuranceState: MarineInsuranceState,
  ) {}

  public get isVerificationCodeExpired(): boolean {
    if (this.formGroup.get('phoneNumber').invalid || !this.formGroup.get('phoneNumber').value) {
      return true;
    }
    return VerificationCodeHelper.isVerificationCodeExpired(this.formattedPhoneNumber);
  }

  public ngOnInit(): void {
    this.user = this.companiesService.getUser();
    this.company = this.companiesService.getCompany();
    this.formGroup = this.fb.group({
      phoneNumber: new UntypedFormControl(this.user.phoneNumber || null, [Validators.maxLength(15)]),
    });

    this.formGroup
      .get(['phoneNumber'])
      .valueChanges.pipe(takeUntil(this.isDestroy))
      .subscribe((data) => {
        if (data) {
          this.formattedPhoneNumber = data;
          this.isChangedPhoneNumber =
            this.user.phoneNumberConfirmed && data !== undefined && this.formattedPhoneNumber !== this.user.phoneNumber;
        } else {
          this.formattedPhoneNumber = undefined;
        }
      });
    this.errors = {
      phoneNumber: {
        maxlength: 'FORM_ERROR.MAX_LENGTH',
        validatePhoneNumber: 'FORM_ERROR.NOT_VALID_NUMBER',
      },
    };

    if (!this.currIsf && !this.currES && !this.isDelivery && !this.currBondRequest) {
      this.router.navigate(['dashboard']);
      return;
    }
    if (!!this.error) {
      this.isError = true;
    }

    if (!!this.warning) {
      this.isWarningRes = true;
    }

    if (this.currIsf && this.currIsf.id) {
      this.configureIsfState();
    } else if (this.isDelivery || this.currES || this.currBondRequest) {
      if (!this.isError && !this.isWarningRes) {
        this.isSuccessRes = true;
      }
    }

    this.marineInsuranceState.resetData();
    if (this?.currIsf || this?.currES) {
      this.loadInsurance();
    }
  }

  public ngOnChanges(simpleChanges: SimpleChanges): void {
    if (simpleChanges.currIsf || simpleChanges.currES) {
      this.loadInsurance();
    }
  }

  public ngOnDestroy(): void {
    this.isDestroy.next();
    this.isDestroy.complete();
  }

  public goToSummary() {
    this.goToEdit.emit();
  }

  public goHome() {
    this.router.navigate(['dashboard']);
  }

  public skip(): void {}

  public continue(): void {}

  public openVerifyModal() {
    if (!this.formGroup.get('phoneNumber').invalid && this.formattedPhoneNumber) {
      this.hiddeninput.nativeElement.focus();
      this.updatePhoneNumber();
      this.verify();
    }
  }

  public successPhoneVerification() {
    this.user = this.companiesService.getUser();
    this.formGroup.get('phoneNumber').patchValue(this.user.phoneNumber);
    this.isPhoneUpdated = true;
  }

  public checkValidState(): void {}

  public openMarineInsuranceQuoteForm(isEdit?: boolean): void {
    this.marineInsuranceSideformService.onOpenQuoteSidebar(
      !!this.currIsf ? enumMarineInsuranceEntityType.ISF : enumMarineInsuranceEntityType.ENTRY_SUMMARY,
      {
        [!!this.currIsf ? 'isf' : 'entrySummary']: !!this.currIsf ? this.currIsf : this.currES,
      },
      this.marineInsuranceState.currentInsurance.getValue(),
      isEdit,
    );
  }

  public get isInsuranceAvailable(): Observable<boolean> {
    return this.marineInsuranceState.isInsuranceAvailable;
  }

  public get isInsuranceLoading(): Observable<boolean> {
    return this.marineInsuranceState.isInsuranceLoading;
  }

  public get currentInsurance(): BehaviorSubject<MarineInsuranceModel> {
    return this.marineInsuranceState.currentInsurance;
  }

  public get currentInsuranceDocuments(): BehaviorSubject<DocumentModel> {
    return this.marineInsuranceState.currentInsuranceDocuments;
  }

  public get currentInsurancePendingBills(): BehaviorSubject<PaymentBillItemPendingModel> {
    return this.marineInsuranceState.currentInsurancePendingBills;
  }

  public get parentEntity(): IsfModel | EntrySummaryModel {
    return !!this.currIsf ? (this.currIsf as unknown as IsfModel) : this.currES;
  }

  private verify() {
    this.verifyModal.openModal(this.formattedPhoneNumber, this.user);

    if (this.isVerificationCodeExpired) {
      this.companiesService
        .phoneConfirmation(this.formattedPhoneNumber)
        .pipe(takeUntil(this.isDestroy))
        .subscribe(() => {
          VerificationCodeHelper.setVerificationCode(this.formattedPhoneNumber);
        });
    }
  }

  private updatePhoneNumber() {
    if (this.formattedPhoneNumber === this.companiesService.getUser().phoneNumber) {
      return;
    }
    this.companiesService
      .updateUser({ ...this.companiesService.getUser(), phoneNumber: this.formattedPhoneNumber })
      .pipe(
        switchMap(() => this.companiesService.getUserData()),
        takeUntil(this.isDestroy),
      )
      .subscribe();
  }

  private configureIsfState() {
    if (!this.isError) {
      const isState = [enumIsfState.NOT_VALID_AT_PROVIDER, enumIsfState.BOND_PENDING, enumIsfState.PROVIDER_SEND_PENDING].includes(
        this.currIsf.state,
      );
      const isPaymentState = [enumPaymentState.SUCCESS, enumPaymentState.DELAYED].includes(this.currIsf.paymentState);

      if (isState || isPaymentState) {
        this.isSuccessRes = true;
        return;
      }
      this.isWarningRes = true;
    }
  }

  private loadInsurance(): void {
    const method: any = !!this.currIsf
      ? this.isfStateService.getInsuranceAvailability(this.currIsf?.referenceNumber)
      : this.entrySummaryStateService.getInsuranceAvailability(this.currES);

    method
      .pipe(
        switchMap((data: { insurance: MarineInsuranceModel; isAvailable: boolean }) =>
          combineLatest([
            of(data),
            !!data.insurance?.referenceNumber
              ? this.entrySummaryStateService.getInsurancePendingBills(this.company?.id, data.insurance.referenceNumber)
              : of(EMPTY),
          ]),
        ),
        takeUntil(this.isDestroy),
      )
      .subscribe(
        ([data, pendingBills]: [{ insurance: MarineInsuranceModel; isAvailable: boolean }, PaymentBillItemPendingModel]) => {
          this.marineInsuranceState.saveData(data.insurance, data.isAvailable, false);
          this.marineInsuranceState.saveBillsData(pendingBills);
        },
        () => this.marineInsuranceState.saveData(null, true, false),
      );
  }
}
